import {
  Component,
  Host,
  h,
  State,
  Prop,
  Listen,
  Element,
  Event,
  EventEmitter,
} from '@stencil/core';
import {
  DateUtil,
  SessionModel,
  SessionHelper,
  Language,
  SessionStayPeriod,
  SessionGuests,
  SessionDisplay,
  DeepLinkData,
} from '@cesare';
import { QwButton } from '../shared/qw-button/qw-button';
import { cloneDeep } from 'lodash-es';
import {
  DEFAULT_LOCATION,
  QW_ROOM_TYPE_LIST,
  QwBookinBarInputType,
  QwLocation,
  QwRoomTypeType,
  LOCATIONS,
} from '../../index';

import { getPromoCodeValueFromQueryParams } from '../../core/promo-code/promo-code.service';
import {
  getSessionSettingsFromSessionStorage,
  sessionStorageGetItem,
  sessionStorageSetItem,
  removeFusionIdFromSessionStorage,
} from '../../core/storage/storage.service';
import {
  createDeepLink,
  getSessionFromSessionStorageOrEnv,
  isInGroup,
  setBookingInProgress,
  setSessionSettingsToSessionStorage,
} from '../../core/app/app';
import { isRunningLocally } from '../../core/utils';
import { IS_MOBILE, stopBodyScroll } from '../../global/app';

export interface QwServiceInfo {
  title: string;
  details: string;
  imageSrc: string;
}
export interface QwServiceMessage {
  hotel_with_message?: string;
  hotel_id?: string;
  hotel_message: string;
}

@Component({
  tag: 'qw-update-search',
  styleUrl: 'qw-update-search.scss',
  shadow: false,
})
export class QwUpdateSearch {
  @Element() el: HTMLElement;
  @Prop() qwUpdateSearchTitle?: string;
  @Prop() qwUpdateSearchServiceInfo?: any;
  @Prop() qwUpdateSearchMessage?: any;
  @Prop() qwUpdateSearchMessages?: any;
  @Prop() qwUpdateSearchDefaultLocation: any;
  @Prop() qwUpdateSearchChildrenComponents: NodeListOf<HTMLElement>;
  @State() roomTypeLabel: QwRoomTypeType['label'];
  @State() qwOpenModal: boolean = false;
  @State() sessionSettings: any;
  @State() selectedDates: string;
  @State() selectedGuests: string;
  @State() tempGuests: SessionGuests;
  @State() tempDates: SessionStayPeriod;
  @State() selectedLocationName: string;
  @State() selectedLocationValue: string;
  @State() session: SessionModel;
  @State() sessionToSend: SessionModel;
  @State() hasCurrencyChange: SessionDisplay['currency'];
  @Event() qwEmitGtmData: EventEmitter<any>;

  public componentWillLoad() {
    this.session = getSessionFromSessionStorageOrEnv();
    this.sessionToSend = getSessionFromSessionStorageOrEnv();
    this.sessionSettings = getSessionSettingsFromSessionStorage();
    this.tempGuests = this.session.context.guests;
    this.tempDates = this.session.context.stayPeriod;
    this.selectedDates =
      SessionHelper.formatStayPeriod(this.session) ||
      Language.getTranslation('whereAreYouGoing');
    this.selectedGuests = this.getGuestsLabel(this.session);

    const promoCodeValue = getPromoCodeValueFromQueryParams();
    if (promoCodeValue) {
      sessionStorageSetItem('promoCode', promoCodeValue);
    }
    this.qwUpdateSearchDefaultLocation = this.qwUpdateSearchDefaultLocation
      ? JSON.parse(this.qwUpdateSearchDefaultLocation)
      : LOCATIONS;

    const selectedLocation = this.qwUpdateSearchDefaultLocation.hostel.find(
      (h) => h.hotel_id == this.session.context.selectedHotelId,
    );

    if (selectedLocation && !isInGroup()) {
      this.selectedLocationName = selectedLocation?.name;
      this.selectedLocationValue = selectedLocation?.value;
    } else {
      this.selectedLocationName = '';
      this.selectedLocationValue = 'root';
    }

    this.roomTypeLabel =
      this.getRoomTypeLabel(sessionStorageGetItem('roomType')) ||
      Language.getTranslation('allRooms');

    this.qwUpdateSearchServiceInfo =
      this.qwUpdateSearchServiceInfo && !isRunningLocally()
        ? (JSON.parse(this.qwUpdateSearchServiceInfo) as QwServiceInfo)
        : false;

    this.qwUpdateSearchMessage =
      this.qwUpdateSearchMessage != undefined
        ? (JSON.parse(this.qwUpdateSearchMessage) as QwServiceMessage)
        : false;
    this.qwUpdateSearchMessages =
      this.qwUpdateSearchMessages != undefined
        ? (JSON.parse(this.qwUpdateSearchMessages) as Array<QwServiceMessage>)
        : false;
  }

  public componentDidLoad() {
    this.qwUpdateSearchChildrenComponents = this.el.querySelectorAll(
      '.qw-modal--component',
    );
    this.el.addEventListener('click', this.handleClickOutside);
  }
  public emitGtmData() {
    if (!this.sessionSettings) {
      this.sessionSettings = getSessionSettingsFromSessionStorage();
    }
    const gtmData = {
      event: 'GA4event',
      eventName: 'booking_form_submit',
      event_data: {
        hotel_name: this.sessionSettings?.location.value,
        accomodation_type:
          this.sessionSettings?.roomType?.label ||
          sessionStorageGetItem('roomType') ||
          Language.getTranslation('allRooms'),
        adults_nb: this.sessionSettings?.guests?.adults || 1,
        children_nb: this.sessionSettings?.guests?.children || 0,
      },
    };
    this.qwEmitGtmData.emit(gtmData);
  }

  private handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    const targetHtml = event.target as HTMLElement;
    const clickedInsideComponent = Array.from(
      this.qwUpdateSearchChildrenComponents,
    ).some((componentEl) => componentEl.contains(target));

    if (!clickedInsideComponent && targetHtml.tagName !== 'SPAN') {
      this.closeComponents();
    }
  };

  private getGuestsLabel(session: SessionModel) {
    return (
      `${SessionHelper.getTotalGuests(session)} ${this.getSingularLabel(
        SessionHelper.getTotalGuests(session),
      )}` || Language.getTranslation('guests')
    );
  }

  @Listen('changeLocationLink', { target: 'window' })
  public onQwChangeLocationLink(e: CustomEvent<QwLocation>) {
    this.closeComponents();
    this.selectedLocationName = e.detail.name;
    this.selectedLocationValue = e.detail.value;
    this.sessionToSend.context.selectedHotelId = e.detail.hotelId;
    this.sessionToSend.engineId = e.detail.engineId;
  }
  @Listen('qwOpenBookigModal', { target: 'window' })
  public toggleModaSearch() {
    this.toggleModal();
  }

  @Listen('qwCalendarChange', { target: 'window' })
  public onQwCalendarChange(e: CustomEvent<SessionStayPeriod>) {
    this.sessionToSend.context.stayPeriod = e.detail;
    this.tempDates = e.detail;
    const arrivalDate = DateUtil.removeTimeFromDate(
      new Date(e.detail.arrivalDate),
    );
    const departureDate = DateUtil.removeTimeFromDate(
      new Date(e.detail.departureDate),
    );
    const formattedArrivalDate = DateUtil.formatCalendarDate(
      arrivalDate,
      sessionStorageGetItem('lang_js') || 'en',
    );
    const formattedDepartureDate = DateUtil.formatCalendarDate(
      departureDate,
      sessionStorageGetItem('lang_js') || 'en',
    );
    this.selectedDates = `${formattedArrivalDate} - ${formattedDepartureDate}`;
  }

  @Listen('roomTypeChange', { target: 'window' })
  public onQwRoomTypeChange(e: CustomEvent<QwRoomTypeType>) {
    this.closeComponents();
    this.roomTypeLabel = e.detail.label;
  }

  @Listen('qwGuestChange', { target: 'window' })
  public onQwGuestChange(e: CustomEvent) {
    this.sessionToSend.context.guests = e.detail;
    this.tempGuests = e.detail;
    let sessionTemp = cloneDeep(this.session);
    sessionTemp.context.guests = e.detail;
    this.selectedGuests = this.getGuestsLabel(sessionTemp);
  }

  private onClickInput(compontentType: QwBookinBarInputType) {
    const component = this.el.querySelector(`${compontentType}`) as HTMLElement;
    const tagName = component?.tagName.toLowerCase();
    this.closeComponents(tagName);
    component?.classList.toggle('qw-component--show');
  }

  private getRoomTypeLabel(roomType) {
    return QW_ROOM_TYPE_LIST.find((r) => r.value === roomType)?.label;
  }

  private closeComponents(componentAlreadyOpenType?: string) {
    this.el.querySelectorAll(`.qw-component--show`).forEach((el) => {
      if (el.tagName.toLowerCase() != componentAlreadyOpenType) {
        el.classList.remove('qw-component--show');
      }
    });
  }

  public getSingularLabel(guestNumber) {
    return parseInt(guestNumber) === 1
      ? Language.getTranslation('guest')
      : Language.getTranslation('guests');
  }

  public toggleModal() {
    this.qwOpenModal = !this.qwOpenModal;
    stopBodyScroll(this.qwOpenModal);
  }

  @Listen('qwCurrencyChanged', { target: 'window' })
  public currencyChanged(event: CustomEvent<SessionDisplay['currency']>) {
    this.hasCurrencyChange = event.detail;
  }
  @Listen('qwCurrencyAlreadyChanged', { target: 'window' })
  public currencyAlreadyChanged(
    event: CustomEvent<SessionDisplay['currency']>,
  ) {
    this.hasCurrencyChange = event.detail;
  }

  private book() {
    removeFusionIdFromSessionStorage();
    const hotelId = this.sessionToSend.context.selectedHotelId;
    const location = this.qwUpdateSearchDefaultLocation.hostel.find(
      (l) => Number(l.hotel_id) == hotelId,
    );
    setBookingInProgress({
      hotelValue: location.value,
    });
    if (isRunningLocally()) {
      sessionStorage.removeItem('fusionId');
      sessionStorage.removeItem('promoCode');
      sessionStorage.removeItem('sessionSettings');

      setSessionSettingsToSessionStorage(
        {
          guests: this.sessionToSend.context.guests,
          stayPeriod: this.sessionToSend.context.stayPeriod,
        },
        this.sessionToSend.context.selectedHotelId,
      );

      window.location.assign('/pages/rooms');
    }
    if (!isRunningLocally()) {
      let dataObject: DeepLinkData = {
        property: location.value,
        guests: this.sessionToSend.context.guests,
        stayPeriod: this.sessionToSend.context.stayPeriod,
        lang: sessionStorage.getItem('lang_js') || 'en',
        promoCode: sessionStorage.getItem('promoCode'),
      };
      if (this.hasCurrencyChange) {
        dataObject.currency = this.hasCurrencyChange;
      }

      const url = createDeepLink(dataObject);

      window.location.assign(url);
      return;
    }
  }

  render() {
    return (
      <Host class={`qw-modal ${this.qwOpenModal ? 'is-open' : ''}`}>
        <div class="qw-modal--container">
          <div class="qw-modal--close" onClick={() => this.toggleModal()}></div>
          {!isInGroup() && this.qwUpdateSearchMessage && IS_MOBILE && (
            <div
              class="qw-modal--message service-message"
              data-service-message-id={this.qwUpdateSearchMessage.hotel_id}
            >
              <p class="qw-modal--message__text">
                {this.qwUpdateSearchMessage.hotel_message}
              </p>
            </div>
          )}
          {this.qwUpdateSearchMessages &&
            isInGroup() &&
            IS_MOBILE &&
            this.qwUpdateSearchMessages.map((el) => (
              <div
                class="qw-modal--message service-message"
                data-service-message-id={el.hotel_with_message}
              >
                <p class="qw-modal--message__text">{el.hotel_message}</p>
              </div>
            ))}
          <div class="qw-modal--content">
            {this.qwUpdateSearchTitle && !IS_MOBILE && (
              <h4>{this.qwUpdateSearchTitle}</h4>
            )}
            <div class="qw-update-search__fields">
              {!IS_MOBILE && (
                <qw-calendar
                  qw-calendar-id="calendar-update-search"
                  qwLocationsList={JSON.stringify(
                    this.qwUpdateSearchDefaultLocation,
                  )}
                  class="qw-modal--component"
                />
              )}
              <div class="qw-update-search--input-wrapper">
                <qw-input
                  qwSpanEditable={true}
                  onClick={() =>
                    this.onClickInput(QwBookinBarInputType.Location)
                  }
                  qwInputIsReadonly={true}
                  qwInputLabel={Language.getTranslation('destination')}
                  qwInputHasSelectArrow={true}
                  qwInputValue={
                    this.selectedLocationName ||
                    Language.getTranslation('pleaseSelect')
                  }
                />
                <qw-location-list
                  current-lang={sessionStorageGetItem('lang_js') || 'en'}
                  class="qw-modal--component"
                  qwLocationListLocations={this.qwUpdateSearchDefaultLocation}
                />
              </div>
              <div class="qw-update-search--input-wrapper">
                <qw-input
                  qwSpanEditable={true}
                  onClick={() => this.onClickInput(QwBookinBarInputType.Date)}
                  qwInputIsReadonly={true}
                  qwInputHasSelectArrow={true}
                  qwInputLabel={Language.getTranslation('dates')}
                  qwInputValue={this.selectedDates}
                />
                {IS_MOBILE && (
                  <qw-calendar
                    qw-calendar-id="calendar-update-search"
                    class="qw-modal--component"
                    qwLocationsList={JSON.stringify(
                      this.qwUpdateSearchDefaultLocation,
                    )}
                  />
                )}
              </div>
              <div class="qw-update-search--input-wrapper">
                <qw-input
                  qwSpanEditable={true}
                  onClick={() => this.onClickInput(QwBookinBarInputType.Guest)}
                  qwInputIsReadonly={true}
                  qwInputLabel={Language.getTranslation('guests')}
                  qwInputHasSelectArrow={true}
                  qwInputValue={this.selectedGuests}
                />
                <qw-guest class="qw-modal--component" />
              </div>
              <div class="qw-update-search--input-wrapper">
                <qw-input
                  onClick={() =>
                    this.onClickInput(QwBookinBarInputType.RoomType)
                  }
                  qwInputIsReadonly={true}
                  qwSpanEditable={true}
                  qwInputLabel={Language.getTranslation('roomType')}
                  qwInputHasSelectArrow={true}
                  qwInputValue={this.roomTypeLabel}
                />
                <qw-room-type-list class="qw-modal--component" />
              </div>
            </div>

            {this.qwUpdateSearchServiceInfo && (
              <div class="qw-update-search__info">
                {this.qwUpdateSearchServiceInfo.imageSrc && !IS_MOBILE && (
                  <img
                    class="qw-update-search__info--image"
                    src={this.qwUpdateSearchServiceInfo.imageSrc}
                    alt={`Image ${this.qwUpdateSearchTitle}`}
                    title={`Image ${this.qwUpdateSearchTitle}`}
                  />
                )}
                {this.qwUpdateSearchServiceInfo.title && (
                  <h6 class="qw-update-search__info--title">
                    {this.qwUpdateSearchServiceInfo.title}
                  </h6>
                )}
                {this.qwUpdateSearchServiceInfo.details && (
                  <p class="qw-update-search__info--details">
                    {this.qwUpdateSearchServiceInfo.details}
                  </p>
                )}
              </div>
            )}
            <QwButton
              QwButtonLabel={Language.getTranslation('updateSession')}
              QwButtonDisabled={this.selectedLocationValue === DEFAULT_LOCATION}
              QwButtonOnClick={() => {
                this.book();
                this.emitGtmData();
              }}
            />
          </div>
        </div>
      </Host>
    );
  }
}
